<i18n>
{
  "en": {
    "events": "Events",
    "add": "Add",
    "calendar": "Display calendar",
    "deleteEventTitle": "Do you want to delete event: \"{0}\" ?",
    "deleteEventDescription": "This can not be undone",
    "delete": "Delete",
    "createEventError": "Error creating event. Please, try again later.",
    "createEventSuccess": "Created event \"{0}\" !",
    "editEventSuccess": "Event \"{0}\" was successfully edited!",
    "editEventError": "Error editing event \"{0}\". Please, try again later.",
    "deleteEventSuccess": "Event \"{0}\" was deleted",
    "deleteEventError": "Error deleting event \"{0}\". Please, try again later.",
    "eventTypeList": "Open event type list"
  },
  "es": {
    "events": "Eventos",
    "add": "Añadir",
    "calendar": "Mostrar calendario",
    "deleteEventTitle": "¿Desea borrar el evento \"{0}\" ?",
    "deleteEventDescription": "Este cambio es irreversible",
    "delete": "Eliminar",
    "createEventError": "Error creando el event. Inténtelo de nuevo más tarde.",
    "createEventSuccess": "Creado el evento \"{0}\" !",
    "editEventSuccess": "El evento \"{0}\" se editó correctamente",
    "editEventError": "Error editando el evento \"{0}\". Inténtelo de nuevo más tarde.",
    "deleteEventSuccess": "El evento \"{0}\" fue borrado",
    "deleteEventError": "Error al borrar el evento \"{0}\". Inténtelo de nuevo más tarde.",
    "eventTypeList": "Abrir lista de tipos de evento"
  }
}
</i18n>
<template>
  <div>
    <div class="d-flex flex-wrap mx-7 mt-3 mb-3">
      <v-row>
        <v-col cols="12" align-self="center" class="pa-0 my-4">
          <div class="text-center text-h5">
            {{ $t('events') }}
          </div>
        </v-col>
        <event-list-filter
          @pointOfSale="onPointOfSaleFilter"
          @eventType="onEventTypeFilter"
          @eventSearch="onEventSearch"
        ></event-list-filter>
        <v-col align-self="center" class="pa-0 te d-flex justify-end">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon class="plus-icon mr-5" v-on="on" v-bind="attrs"
                     @click="openModal">
                <v-icon color="grey" large>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('add') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon class="calendar-icon mr-5" v-on="on" v-bind="attrs"
                     @click="openCalendar">
                <v-icon color="grey" large>mdi-calendar</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('calendar') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon class="list-icon mr-5" v-on="on" v-bind="attrs"
                     @click="openEventTypeList">
                <v-icon color="grey" large>mdi-format-list-bulleted</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('eventTypeList') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <v-divider/>
    <v-list>
      <div v-if="filteredEventList">
        <event-row v-for="event in filteredEventList" :key="event.guid" :event="event"
                   @edit="onEdit(event)"
                   @delete="onDelete(event)"
        >
        </event-row>
      </div>
    </v-list>
    <event-create-edit-modal
      :visible="createEventModal"
      @confirm="onConfirmEvent"
      @cancel="onCloseModal"
      :event="eventToCreate"
      type="create"
    />
    <event-create-edit-modal
      :visible="editEventModal"
      @confirm="onEditEvent"
      @cancel="onCloseModal"
      :event="event"
      type="edit"
    />
    <occurrence-calendar-modal
      :visible="calendarModal"
      @cancel="onCloseModal"
    />
    <event-type-list-modal
      :visible="eventTypeListModal"
      @cancel="onCloseModal"
    />
    <dynameat-confirmation-modal :visible="eventDeleteModal"
                                 :title="$t('deleteEventTitle', [eventToDeleteTitle])"
                                 :description="$t('deleteEventDescription')"
                                 :confirm-text="$t('delete')" @confirm="onDeleteEvent"
                                 @cancel="onCloseModal"/>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import { Event } from '@eventManager/models';
import { Watch } from 'vue-property-decorator';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import { normalizeStr } from '@core/helpers';
import EventRow from '../components/eventRow.vue';
import EventCreateEditModal from '../components/EventCreateEditModal.vue';
import OccurrenceCalendarModal from '../components/OccurrenceCalendarModal.vue';
import EventTypeListModal from '../components/EventTypeListModal.vue';
import EventListFilter from '../components/EventListFilter.vue';

@Component
@Components({
  EventRow,
  EventCreateEditModal,
  OccurrenceCalendarModal,
  DynameatConfirmationModal,
  EventListFilter,
  EventTypeListModal
})
@MapActions(['eventTypeListRequest', 'eventListRequest', 'createEventRequest', 'updateEventRequest',
  'deleteEventRequest', 'eventOccurrenceCalendarListRequest', 'addNotification',
  'resetEventOccurrenceCalendarList'])
@MapGetters(['eventList', 'eventTypeList', 'createEventSuccess', 'updateEventSuccess',
  'deleteEventSuccess'])
export default class EventList extends Vue {
  createEventModal = false;

  editEventModal = false;

  eventDeleteModal = false;

  calendarModal = false;

  eventTypeListModal = false;

  event = null;

  eventToCreate = null;

  eventToDelete = null;

  pointOfSaleFilter = [];

  eventTypeFilter = [];

  eventFilter = ''

  normalizedNamesMap = new Map();

  async created() {
    await this.eventTypeListRequest();
    await this.eventListRequest();
    await this.eventOccurrenceCalendarListRequest();
    this.initializeNormalizedNamesMap();
  }

  get filteredEventList() {
    let eventListFiltered = this.eventList;
    if (this.pointOfSaleFilter.length > 0) {
      eventListFiltered = eventListFiltered.filter((item) => {
        const pos = item.posGuidList.filter((each) => this.pointOfSaleFilter.includes(each));
        return pos.length > 0;
      });
    }
    if (this.eventTypeFilter.length > 0) {
      eventListFiltered = eventListFiltered.filter((item) => this.eventTypeFilter.includes(item.eventTypeGuid));
    }

    if (this.eventFilter !== '' && this.normalizedNamesMap.size) {
      const token = normalizeStr(this.eventFilter.trim());
      eventListFiltered = eventListFiltered
        .filter((el) => this.normalizedNamesMap.get(el.name)?.normalizedEventName?.includes(token));
    }
    return eventListFiltered;
  }

  get eventToDeleteTitle() {
    return this.eventToDelete ? this.eventToDelete.name : '';
  }

  onEdit(event) {
    const props = event.serialize();
    this.event = Event.toInstance(props);
    this.editEventModal = true;
  }

  onDelete(event) {
    this.eventToDelete = event;
    this.eventDeleteModal = true;
  }

  initializeNormalizedNamesMap() {
    this.eventList.forEach((el) => {
      this.normalizedNamesMap.set(el.name, {
        normalizedEventName: normalizeStr(el.name)
      });
    });
  }

  openModal() {
    this.eventToCreate = Event.toInstance();
    this.createEventModal = true;
  }

  openCalendar() {
    this.calendarModal = true;
  }

  openEventTypeList() {
    this.eventTypeListModal = true;
  }

  async onConfirmEvent(event) {
    try {
      await this.createEventRequest(event);
      this.addNotification(createSuccessNotification(this.$t('createEventSuccess', [event.name]), null, 3000));
      this.resetEventOccurrenceCalendarList();
      await this.eventOccurrenceCalendarListRequest();
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('createEventError', [event.name])));
    } finally {
      this.onCloseModal();
      this.eventToCreate = null;
    }
  }

  async onEditEvent(event) {
    try {
      await this.updateEventRequest(event);
      this.addNotification(createSuccessNotification(this.$t('editEventSuccess', [event.name]), null, 3000));
      this.resetEventOccurrenceCalendarList();
      await this.eventOccurrenceCalendarListRequest();
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('editEventError', [event.name])));
    } finally {
      this.onCloseModal();
      this.event = null;
    }
  }

  async onDeleteEvent() {
    try {
      await this.deleteEventRequest(this.eventToDelete);
      this.addNotification(createSuccessNotification(this.$t('deleteEventSuccess', [this.eventToDelete.name]), null, 3000));
      this.resetEventOccurrenceCalendarList();
      await this.eventOccurrenceCalendarListRequest();
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('deleteEventError', [this.eventToDelete.name])));
    } finally {
      this.onCloseModal();
      this.eventToDelete = null;
    }
  }

  onCloseModal() {
    this.createEventModal = false;
    this.editEventModal = false;
    this.eventDeleteModal = false;
    this.calendarModal = false;
    this.eventTypeListModal = false;
  }

  onPointOfSaleFilter(string) {
    this.pointOfSaleFilter = string;
  }

  onEventTypeFilter(string) {
    this.eventTypeFilter = string;
  }

  onEventSearch(string) {
    this.eventFilter = string;
  }

  @Watch('createEventSuccess')
  async onCreateEventSuccessChange(newValue) {
    if (newValue) {
      await this.eventListRequest();
      await this.eventOccurrenceCalendarListRequest();
    }
  }

  @Watch('updateEventSuccess')
  async onUpdateEventSuccessChange(newValue) {
    if (newValue) {
      await this.eventListRequest();
      await this.eventOccurrenceCalendarListRequest();
    }
  }

  @Watch('deleteEventSuccess')
  async onDeleteEventSuccessChange(newValue) {
    if (newValue) {
      await this.eventListRequest();
      await this.eventOccurrenceCalendarListRequest();
    }
  }
}
</script>

<style scoped>

</style>
